import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import api from "../api";

const CoverPickerModal = ({ isOpen, onClose, event, onCoverSelected }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [setting, setSetting] = useState(null); // image_name currently being applied

  useEffect(() => {
    if (!isOpen || !event?.event_id) return;
    setLoading(true);
    api
      .get(`/images?eid=${event.event_id}`)
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setImages(raw.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, event?.event_id]);

  const handleSelect = async (imageName) => {
    if (setting) return;
    setSetting(imageName);
    try {
      const res = await api.post(
        `/event_cover/from_gallery?eid=${event.event_id}&image_name=${encodeURIComponent(imageName)}`
      );
      onCoverSelected(res.data.cover_image_url);
      onClose();
    } catch (err) {
      console.error("Failed to set cover from gallery:", err);
    } finally {
      setSetting(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1060,
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          zIndex: 1065,
          width: "min(92vw, 700px)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "18px 20px",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>
            {t("events_admin.cover_picker_title")}
          </span>
          <button
            onClick={onClose}
            style={{
              border: "none", background: "none", cursor: "pointer",
              color: "#9ca3af", fontSize: "16px", padding: "4px 6px",
              borderRadius: "6px", lineHeight: 1,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "16px", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
              <Spinner animation="border" size="sm" style={{ color: "#6366f1" }} />
            </div>
          ) : images.length === 0 ? (
            <div
              style={{
                textAlign: "center", color: "#9ca3af",
                padding: "48px 20px", fontSize: "13px",
              }}
            >
              {t("events_admin.cover_picker_empty")}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                gap: "8px",
              }}
            >
              {images.map((img) => {
                const isActive = setting === img.name;
                return (
                  <div
                    key={img.name}
                    onClick={() => handleSelect(img.name)}
                    title={img.name}
                    style={{
                      aspectRatio: "1 / 1",
                      borderRadius: "10px",
                      overflow: "hidden",
                      cursor: setting ? "wait" : "pointer",
                      position: "relative",
                      border: "2px solid transparent",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!setting) {
                        e.currentTarget.style.borderColor = "#6366f1";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "transparent";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <img
                      src={`${process.env.REACT_APP_MODEL_ENDPOINT}/images/${img.name}?eid=${event.event_id}&thumbnail=true`}
                      alt={img.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      loading="lazy"
                    />
                    {isActive && (
                      <div
                        style={{
                          position: "absolute", inset: 0,
                          background: "rgba(99,102,241,0.65)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <Spinner animation="border" size="sm" style={{ color: "#fff", borderWidth: "2px" }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CoverPickerModal;
